import { IButtonStyles } from '@fluentui/react';
import {
  concatStyleSets,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  IStyle,
  ITheme,
} from '@fluentui/react/lib/Styling';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';

import { AppGalleryStyles, getAppGalleryStyles } from '@1js/acui-gallery';
import { isThemingEnabled } from '@1js/acui-theme';

export const galleryButtonDimensions = 72;
export const lineClassName = 'galleryLine';

export interface AppListGalleryButtonStyles {
  root: IStyle;
  levels: IStyle[];
  line: IStyle;
}

const getListRootStyles = (rtl: boolean): IStyle => {
  let styles: IStyle;
  if (rtl) {
    styles = {
      direction: 'rtl',
      textAlign: 'right /* @noflip */',
    };
  } else {
    styles = {
      direction: 'ltr',
      textAlign: 'left /* @noflip */',
    };
  }

  return { ...styles, width: galleryButtonDimensions, overflow: 'hidden' };
};

const getListLevelStyles = (rtl: boolean, multiLevel: boolean): IStyle[] => {
  if (!multiLevel) {
    return [{ position: 'relative' }, { position: 'relative' }, { position: 'relative' }];
  }

  const margin: string = rtl ? 'marginRight' : 'marginLeft';
  return [
    { position: 'relative', [margin]: '0px /* @noflip */' },
    { position: 'relative', [margin]: '7px /* @noflip */' },
    { position: 'relative', [margin]: '14px /* @noflip */' },
  ];
};

const lineHoverFocusStyle: IStyle = {
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: 'HighlightText',
      ...getHighContrastNoAdjustStyle(),
    },
  },
};

const lineStyles = (theme: ITheme): IStyle => {
  return [
    lineClassName,
    {
      height: 2,
      width: '100%',
      margin: '0px 4px',
      backgroundColor: isThemingEnabled() ? theme.palette.neutralTertiaryAlt : '#BDBDBD',
      position: 'absolute',
      top: '50%',
      outline: '1px solid transparent',
      selectors: {
        [HighContrastSelector]: {
          backgroundColor: 'ButtonText',
          ...getHighContrastNoAdjustStyle(),
        },
      },
    },
  ];
};

const getBaseGalleryButtonInExpandedStyles: IButtonStyles = {
  root: {
    height: galleryButtonDimensions,
    width: galleryButtonDimensions,
    padding: '15px 4px',
    borderTopWidth: 4,
    borderBottomWidth: 4,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    selectors: {
      [':focus']: {
        [`.${lineClassName}`]: lineHoverFocusStyle,
      },
      [':hover']: {
        [`.${lineClassName}`]: lineHoverFocusStyle,
      },
    },
  },
};

const getCustomizedListGalleryStyles = memoizeFunction(
  (customStyles?: Partial<AppGalleryStyles>): Partial<AppGalleryStyles> => {
    return {
      ...customStyles,
      galleryButtonInExpanded: concatStyleSets(
        getBaseGalleryButtonInExpandedStyles,
        customStyles && customStyles.galleryButtonInExpanded
      ),
    };
  }
);

export const getGalleryStyles = memoizeFunction(
  (theme: ITheme, customStyles?: Partial<AppGalleryStyles>): AppGalleryStyles => {
    return getAppGalleryStyles(theme, getCustomizedListGalleryStyles(customStyles));
  }
);

export const getListGalleryButtonStyles = memoizeFunction(
  (theme: ITheme, rtl: boolean, multiLevel: boolean): AppListGalleryButtonStyles => {
    return {
      root: getListRootStyles(rtl),
      levels: getListLevelStyles(rtl, multiLevel),
      line: lineStyles(theme),
    };
  }
);
